import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Container, Form, Button, Row, Col, Alert, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import './Registration.css';

const API_URL = process.env.REACT_APP_API_URL || '';

interface RegistrationFormData {
  race_type: '5K' | 'TRIATHLON';
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  teammate_name?: string;
  waiver_accept: boolean;
  tshirt_size: string;
  k5_division: string;
  triathlon_division?: string;
  parent_name?: string;
  order_id?: string;
  family_names?: string;
}

// Registration costs for each race type
const REGISTRATION_COSTS = {
  // 5K Categories
  '5K_MALE': 25,
  '5K_FEMALE': 25,
  '5K_FAMILY': 75,

  // Triathlon categories (set these to your real prices)
  // If a division isn't listed here, cost will be 0 and payment will be disabled.
  'TRI_MALE_SOLO': 35,
  'TRI_FEMALE_SOLO': 35,
  'TRI_MALE_TEAM': 35,
  'TRI_FEMALE_TEAM': 35,
  'TRI_COED_TEAM': 35,
  'TRI_RELAY_TEAM': 35,
  'TRI_KIDS_RELAY': 35,
  'TRI_KIDS_BALL_AND_CHAIN': 35,
  'TRI_BALL_AND_CHAIN': 35,
  'TRI_OLDER_THAN_OLE_AND_LENA_TEAM': 35,
};

const Registration: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<RegistrationFormData>({
    defaultValues: {
      race_type: '5K',
    },
  });
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [formData, setFormData] = useState<RegistrationFormData | null>(null);
  
  const dateOfBirth = watch('date_of_birth');
  // Calculate age from date of birth
  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  const age = dateOfBirth ? calculateAge(dateOfBirth) : null;

  const selectedRaceType = watch('race_type');
  const selectedK5Division = watch('k5_division');
  const selectedTriDivision = watch('triathlon_division');

  const getDivisionLabelForPayment = () => {
    if (selectedRaceType === 'TRIATHLON') return selectedTriDivision || '';
    return selectedK5Division || '';
  };

  const getRegistrationCost = () => {
    if (selectedRaceType === 'TRIATHLON') {
      switch (selectedTriDivision) {
        case 'Male and Female Solo':
          return REGISTRATION_COSTS['TRI_MALE_SOLO'];
        case 'Male Team':
          return REGISTRATION_COSTS['TRI_MALE_TEAM'];
        case 'Female Team':
          return REGISTRATION_COSTS['TRI_FEMALE_TEAM'];
        case 'Coed Team':
          return REGISTRATION_COSTS['TRI_COED_TEAM'];
        case 'Relay Team':
          return REGISTRATION_COSTS['TRI_RELAY_TEAM'];
        case 'Kids Relay (15 years and younger)':
          return REGISTRATION_COSTS['TRI_KIDS_RELAY'];
        case 'Kids Ball and Chain (15 years and younger)':
          return REGISTRATION_COSTS['TRI_KIDS_BALL_AND_CHAIN'];
        case 'Ball & Chain':
          return REGISTRATION_COSTS['TRI_BALL_AND_CHAIN'];
        case 'Older than Ole & Lena Team':
          return REGISTRATION_COSTS['TRI_OLDER_THAN_OLE_AND_LENA_TEAM'];
        default:
          return 0;
      }
    }

    switch (selectedK5Division) {
      case 'Male':
        return REGISTRATION_COSTS['5K_MALE'];
      case 'Female':
        return REGISTRATION_COSTS['5K_FEMALE'];
      case 'Family':
        return REGISTRATION_COSTS['5K_FAMILY'];
      default:
        return 0;
    }
  };

  const handleFormSubmit = async (data: RegistrationFormData) => {
    setFormData(data);
    setIsProcessingPayment(true);
  };

  const createOrder = async () => {
    if (!API_URL) {
      setSubmitStatus({
        success: false,
        message: 'Missing REACT_APP_API_URL. Set it in .env and restart `npm start`.',
      });
      setIsProcessingPayment(false);
      throw new Error('Missing REACT_APP_API_URL');
    }
    try {
      const response = await fetch(`${API_URL}/api/payment/create`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: String(getRegistrationCost()),
          description: `${selectedRaceType} - ${getDivisionLabelForPayment()}`
        }),
      });
      if (!response.ok) {
        throw new Error(`Payment create failed: ${response.status}`);
      }
      const order = await response.json();
      return order.orderId;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'Failed to create payment. Please try again.' 
      });
      setIsProcessingPayment(false);
      throw error;
    }
  };

  const onApprove = async (data: { orderID: string }) => {
    console.log(data.orderID);
    try {
      // Capture the PayPal order
      const response = await fetch(`${API_URL}/api/payment/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: data.orderID,
        }),
      });

      const captureData = await response.json();
      console.log('Capture Data: %s', captureData.success); 
      console.log('Form Data: %s', formData);
      if (captureData.status === 'COMPLETED' && formData) {
        // Submit registration with payment confirmation
        const registrationResponse = await fetch(`${API_URL}/api/registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            registration_cost: getRegistrationCost(),
            payment_id: data.orderID,
            payment_status: 'completed',
            order_id: data.orderID
          }),
        });

        const result = await registrationResponse.json();
        
        if (registrationResponse.ok) {
          setSubmitStatus({ success: true, message: 'Registration and payment successful!' });
          setFormData(null);
          setIsProcessingPayment(false);
        } else {
          throw new Error(result.message || 'Registration failed after payment');
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'Payment was processed but registration failed. Please contact support.' 
      });
      setIsProcessingPayment(false);
    }
  };

  const onError = () => {
    setSubmitStatus({ 
      success: false, 
      message: 'Payment processing failed. Please try again.' 
    });
    setIsProcessingPayment(false);
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const tshirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const k5Divisions = ['Male', 'Female', 'Family'];
  const triathlonDivisions = [
    'Male and Female Solo',
    'Male Team',
    'Female Team',
    'Coed Team',
    'Relay Team',
    'Kids Relay (15 years and younger)',
    'Kids Ball and Chain (15 years and younger)',
    'Ball & Chain',
    'Older than Ole & Lena Team',
  ];

  const [selectedTshirtSize, setSelectedTshirtSize] = useState<string>('');
  const [selectedRaceTypeLocal, setSelectedRaceTypeLocal] = useState<'5K' | 'TRIATHLON'>('5K');
  const [selectedK5DivisionLocal, setSelectedK5DivisionLocal] = useState<string>('');
  const [selectedTriDivisionLocal, setSelectedTriDivisionLocal] = useState<string>('');

  const currentCost = getRegistrationCost();
  const hasSelectedDivision =
    selectedRaceTypeLocal === 'TRIATHLON' ? !!selectedTriDivisionLocal : !!selectedK5DivisionLocal;
  const canProceedToPayment = hasSelectedDivision && currentCost > 0;
  const showPriceNotConfiguredWarning = hasSelectedDivision && currentCost === 0;

  return (
    <PayPalScriptProvider options={{ 
      clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
      currency: "USD"
    }}>
      <Container className="py-5">
        <h1 className="mb-4">Race Registration</h1>
        {submitStatus && (
          <Alert variant={submitStatus.success ? 'success' : 'danger'}>
            {submitStatus.message}
          </Alert>
        )}
        
        {!isProcessingPayment ? (
          <Form onSubmit={handleSubmit(handleFormSubmit)}>
            <Row className="g-3">
              {/* Personal Information */}
              <h3>Personal Information</h3>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('first_name', { required: 'First name is required' })}
                    isInvalid={!!errors.first_name}
                  />
                  {errors.first_name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.first_name.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('last_name', { required: 'Last name is required' })}
                    isInvalid={!!errors.last_name}
                  />
                  {errors.last_name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.last_name.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    isInvalid={!!errors.email}
                  />
                  {errors.email && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Date of Birth *</Form.Label>
                  <Form.Control
                    type="date"
                    {...register('date_of_birth', { required: 'Date of birth is required' })}
                    isInvalid={!!errors.date_of_birth}
                  />
                  {errors.date_of_birth && (
                    <Form.Control.Feedback type="invalid">
                      {errors.date_of_birth.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {/* Address */}
              <h3 className="mt-4">Address</h3>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Street Address</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('address')}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('city')}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Select
                    {...register('state')}
                  >
                    <option value="">Select state</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>ZIP Code</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('zip_code')}
                  />
                </Form.Group>
              </Col>

              {/* T-Shirt Size */}
              <Col xs={12} className="mt-4">
                <Form.Group>
                  <Form.Label>T-Shirt Size *</Form.Label>
                  <ButtonGroup>
                    {tshirtSizes.map((size) => (
                      <ToggleButton
                        key={size}
                        id={`tshirt-size-${size}`}
                        type="radio"
                        variant="outline-secondary"
                        value={size}
                        checked={selectedTshirtSize === size}
                        onChange={() => {
                          setSelectedTshirtSize(size);
                          setValue('tshirt_size', size, { shouldValidate: true });
                        }}
                      >
                        {size}
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                  <input type="hidden" {...register('tshirt_size', { required: 'T-Shirt Size is required' })} />
                  {errors.tshirt_size && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {errors.tshirt_size.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {/* Race type */}
              <Col xs={12} className="mt-4">
                <Form.Group>
                  <Form.Label>Race *</Form.Label>
                  <ButtonGroup>
                    {(['5K', 'TRIATHLON'] as const).map((race) => (
                      <ToggleButton
                        key={race}
                        id={`race-type-${race}`}
                        type="radio"
                        variant="outline-secondary"
                        value={race}
                        checked={selectedRaceTypeLocal === race}
                        onChange={() => {
                          setSelectedRaceTypeLocal(race);
                          setValue('race_type', race, { shouldValidate: true });
                          // Reset divisions when switching race type
                          setSelectedK5DivisionLocal('');
                          setSelectedTriDivisionLocal('');
                          setValue('k5_division', '', { shouldValidate: true });
                          setValue('triathlon_division', '', { shouldValidate: true });
                        }}
                      >
                        {race === '5K' ? '5K' : 'Triathlon'}
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                  <input type="hidden" {...register('race_type', { required: 'Race is required' })} />
                  {errors.race_type && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {errors.race_type.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {/* Divisions */}
              <Col xs={12} className="mt-4">
                <Form.Group>
                  <Form.Label>{selectedRaceTypeLocal === 'TRIATHLON' ? 'Triathlon division *' : '5K division *'}</Form.Label>
                  <ButtonGroup style={{ flexWrap: 'wrap' }}>
                    {(selectedRaceTypeLocal === 'TRIATHLON' ? triathlonDivisions : k5Divisions).map((div) => (
                      <ToggleButton
                        key={div}
                        id={`${selectedRaceTypeLocal}-division-${div}`}
                        type="radio"
                        variant="outline-secondary"
                        value={div}
                        checked={
                          selectedRaceTypeLocal === 'TRIATHLON'
                            ? selectedTriDivisionLocal === div
                            : selectedK5DivisionLocal === div
                        }
                        onChange={() => {
                          if (selectedRaceTypeLocal === 'TRIATHLON') {
                            setSelectedTriDivisionLocal(div);
                            setValue('triathlon_division', div, { shouldValidate: true });
                          } else {
                            setSelectedK5DivisionLocal(div);
                            setValue('k5_division', div, { shouldValidate: true });
                          }
                        }}
                      >
                        {div}
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                  {selectedRaceTypeLocal === 'TRIATHLON' ? (
                    <>
                      <input
                        type="hidden"
                        {...register('triathlon_division', { required: 'Triathlon division is required' })}
                      />
                      {errors.triathlon_division && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {errors.triathlon_division.message}
                        </Form.Control.Feedback>
                      )}
                    </>
                  ) : (
                    <>
                      <input type="hidden" {...register('k5_division', { required: '5K division is required' })} />
                      {errors.k5_division && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {errors.k5_division.message}
                        </Form.Control.Feedback>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>

              {/* Team/Partner name */}
              {selectedRaceTypeLocal === 'TRIATHLON' &&
                (selectedTriDivisionLocal.includes('Team') ||
                  selectedTriDivisionLocal.includes('Relay') ||
                  selectedTriDivisionLocal.includes('Ball & Chain')) && (
                  <Col xs={12} className="mt-4">
                    <Form.Group>
                      <Form.Label>Teammate / Partner name *</Form.Label>
                      <Form.Control
                        type="text"
                        {...register('teammate_name', { required: 'Teammate / partner name is required for this division' })}
                        isInvalid={!!errors.teammate_name}
                      />
                      {errors.teammate_name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.teammate_name.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                )}

              {/* Parent Name */}
              {age !== null && age < 18 && (
                <Col xs={12} className="mt-4">
                  <div style={{ marginBottom: '0.5rem' }}>
                    By Entering the Parent's Name of the child being registered, I am attesting that<br />
                    this is the Parent's name and that the Parent has given permission for<br />
                    this child to enter the Triathlon.
                  </div>
                  <Form.Group>
                    <Form.Label>Parent (if under 18)</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('parent_name', { required: 'Parent name is required for registrants under 18' })}
                      isInvalid={!!errors.parent_name}
                    />
                    {errors.parent_name && (
                      <Form.Control.Feedback type="invalid">
                        {errors.parent_name.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              )}

              {/* Waiver */}
              <Col xs={12} className="mt-4">
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="waiver_accept"
                    {...register('waiver_accept', {
                      required: 'You must accept the waiver to register',
                      validate: (v) => v === true || 'You must accept the waiver to register',
                    })}
                    label="I accept the terms and conditions and waiver *"
                  />
                  {errors.waiver_accept && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {errors.waiver_accept.message as string}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {/* Family Names */}
              {selectedRaceTypeLocal === '5K' && selectedK5DivisionLocal === 'Family' && (
                <Col xs={12} className="mt-4">
                  <Form.Group>
                    <Form.Label>Family Members (please list all family members and their tshirt sizes) *</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('family_names', { required: 'Family names are required for Family division' })}
                      isInvalid={!!errors.family_names}
                    />
                    {errors.family_names && (
                      <Form.Control.Feedback type="invalid">
                        {errors.family_names.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              )}

              {showPriceNotConfiguredWarning && (
                <Col xs={12} className="mt-4">
                  <Alert variant="warning" className="mb-0">
                    This division doesn’t have a price configured yet. Update the triathlon prices in this file to enable payment.
                  </Alert>
                </Col>
              )}

              {/* Submit Button */}
              <Col xs={12} className="mt-4">
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={!canProceedToPayment}
                >
                  Proceed to Payment
                </Button>
              </Col>
            </Row>
          </Form>
        ) : (
          <div className="payment-section">
            <h3>Complete Payment</h3>
            <p>Registration Cost: ${getRegistrationCost()}</p>
            <PayPalButtons
              createOrder={createOrder}
              onApprove={onApprove}
              onError={onError}
              style={{ layout: "vertical" }}
            />
            <Button 
              variant="secondary" 
              className="mt-3"
              onClick={() => {
                setIsProcessingPayment(false);
                setFormData(null);
              }}
            >
              Cancel Payment
            </Button>
          </div>
        )}
      </Container>
    </PayPalScriptProvider>
  );
};

export default Registration; 